"use client";

import React from "react";
import { FileText } from "lucide-react";
import { useTicketEditMode } from "@/contexts/TicketEditModeContext";
import { InlineEditableValue } from "@/features/tickets/components/InlineEditableValue";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import {
  MultiUserDisplay,
  readAssignedUserRef,
} from "@/features/tasks/components/components/MultiUserDisplay";
import { TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS } from "@/lib/schemas/task";
import { getTaskStatusColor, getTaskPriorityColor } from "@/lib/schemas/task";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

const statusSelectOptions = TASK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
const prioritySelectOptions = TASK_PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

function EditableOrStatic({
  field,
  value,
  format,
  children,
}: {
  field: string;
  value: number | string | null | undefined;
  format?: "number" | "hours" | "currency" | "text" | "date";
  children: React.ReactNode;
}) {
  const editMode = useTicketEditMode();
  if (editMode?.isEditMode) {
    return (
      <InlineEditableValue
        field={field}
        value={value}
        onSave={async (f, v) => editMode.onUpdate({ [f]: v })}
        format={format ?? "text"}
      />
    );
  }
  return <div className="mt-1 text-sm text-card-foreground">{children}</div>;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

export function TaskInformationSection({ item }: TabContentProps<ContactTaskRow>) {
  const editMode = useTicketEditMode();

  if (!item) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Task Information
        </h3>
        <div className="text-sm text-gray-500">Loading task information...</div>
      </div>
    );
  }

  const status = item.status ?? "";
  const priority = item.priority ?? "";
  const dueDate = item.due_date;
  // `assigned_users` / `tags` are `Maybe<Array<Maybe<string>>>` in codegen — the
  // consumers below only ever handle real strings, so drop the nulls here.
  const assignedUsers = (item.assigned_users ?? []).filter(isNonEmptyString);
  const tags = (item.tags ?? []).filter(isNonEmptyString);
  // `assigned_user` is a JOINED relation, not a column on `contact_tasks`, so it
  // is absent from the codegen row type — read it defensively.
  const assignedUser = readAssignedUserRef(
    "assigned_user" in item ? item.assigned_user : undefined
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Task Information
      </h3>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <span className="text-sm font-medium text-gray-500">Status</span>
          {editMode?.isEditMode ? (
            <ResponsiveSelect
              value={status}
              onValueChange={(value) => editMode.onUpdate({ status: value })}
              options={statusSelectOptions}
              placeholder="Select status"
              title="Status"
              className="mt-1 w-full max-w-xs"
            />
          ) : (
            <div className="mt-1">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskStatusColor(
                  status
                )}`}
              >
                {(TASK_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status) || "—"}
              </span>
            </div>
          )}
        </div>

        <div>
          <span className="text-sm font-medium text-gray-500">Priority</span>
          {editMode?.isEditMode ? (
            <ResponsiveSelect
              value={priority}
              onValueChange={(value) => editMode.onUpdate({ priority: value })}
              options={prioritySelectOptions}
              placeholder="Select priority"
              title="Priority"
              className="mt-1 w-full max-w-xs"
            />
          ) : (
            <div className="mt-1">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getTaskPriorityColor(
                  priority
                )}`}
              >
                {(TASK_PRIORITY_OPTIONS.find((o) => o.value === priority)?.label ?? priority) || "—"}
              </span>
            </div>
          )}
        </div>

        <div>
          <span className="text-sm font-medium text-gray-500">Due date</span>
          <EditableOrStatic field="due_date" value={dueDate ?? null} format="date">
            {dueDate ? new Date(dueDate).toLocaleDateString() : "—"}
          </EditableOrStatic>
        </div>

        <div>
          <span className="text-sm font-medium text-gray-500">Assigned</span>
          <div className="mt-1">
            <MultiUserDisplay
              assignedUsers={assignedUsers}
              assignedUser={assignedUser}
            />
          </div>
        </div>

        {tags && tags.length > 0 && (
          <div>
            <span className="text-sm font-medium text-gray-500">Tags</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-md bg-card-100 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
